import React from 'react'
import { Box, Slider } from '@mui/material'

import { InputLabelStyled, Typography } from '~components/internal'
import { type ELVoice } from '~types'

import VoiceSelectorElevenLabs from './VoiceSelectorElevenLabs'

interface InlineVoiceSliderProps {
  label: string
  value: number | null
  onChange: (v: number) => void
  min: number
  max: number
  defaultValue: number
  minLabel: string
  maxLabel: string
  formatValue: (v: number) => string
  step?: number
}

const InlineVoiceSlider: React.FC<InlineVoiceSliderProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  defaultValue,
  minLabel,
  maxLabel,
  formatValue,
  step = 0.01,
}) => {
  const resolved = value ?? defaultValue
  return (
    <Box>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}
      >
        <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.primary' }}>
          {label}
        </Typography>
        <Typography variant='caption' sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
          {formatValue(resolved)}
        </Typography>
      </Box>
      <Slider
        value={resolved}
        onChange={(_, v) => onChange(v as number)}
        min={min}
        max={max}
        step={step}
        size='small'
        sx={{
          color: 'text.primary',
          py: 0.5,
          '& .MuiSlider-thumb': { width: 14, height: 14 },
          '& .MuiSlider-rail': { opacity: 0.26 },
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.5 }}>
        <Typography variant='caption' sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
          {minLabel}
        </Typography>
        <Typography variant='caption' sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
          {maxLabel}
        </Typography>
      </Box>
    </Box>
  )
}

export interface VoiceConfigElevenLabsProps {
  voiceId: string | null
  onVoiceChange: (voiceId: string | null) => void
  voices: ELVoice[]
  voiceSpeed: number | null
  onVoiceSpeedChange: (v: number) => void
  voiceStability: number | null
  onVoiceStabilityChange: (v: number) => void
  voiceSimilarity: number | null
  onVoiceSimilarityChange: (v: number) => void
  isPreviewLoading: boolean
  isPreviewPlaying: boolean
  onPreviewGreeting: () => void
  greetingMessage: string
}
const VoiceConfigElevenLabs: React.FC<VoiceConfigElevenLabsProps> = ({
  voiceId,
  onVoiceChange,
  voices,
  voiceSpeed,
  onVoiceSpeedChange,
  voiceStability,
  onVoiceStabilityChange,
  voiceSimilarity,
  onVoiceSimilarityChange,
  isPreviewLoading,
  isPreviewPlaying,
  onPreviewGreeting,
  greetingMessage,
}) => (
  <>
    <VoiceSelectorElevenLabs
      selectedVoiceId={voiceId || undefined}
      onSelect={(voice) => onVoiceChange(voice ? voice.voice_id : null)}
      voices={voices}
      placeholder='Select a voice (optional)'
      isPreviewLoading={isPreviewLoading}
      isPreviewPlaying={isPreviewPlaying}
      onPreviewGreeting={onPreviewGreeting}
      greetingMessage={greetingMessage}
    />

    <Box>
      <InputLabelStyled shrink>Voice customizations</InputLabelStyled>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mt: 0.5, px: 1 }}>
        <InlineVoiceSlider
          label='Speed'
          value={voiceSpeed}
          onChange={onVoiceSpeedChange}
          min={0.7}
          max={1.2}
          defaultValue={1.0}
          minLabel='Slower'
          maxLabel='Faster'
          formatValue={(v) => v.toFixed(2)}
        />
        <InlineVoiceSlider
          label='Stability'
          value={voiceStability}
          onChange={onVoiceStabilityChange}
          min={0}
          max={1.0}
          defaultValue={0.5}
          minLabel='Expressive'
          maxLabel='Consistent'
          formatValue={(v) => `${(v * 100).toFixed(0)}%`}
        />
        <InlineVoiceSlider
          label='Similarity'
          value={voiceSimilarity}
          onChange={onVoiceSimilarityChange}
          min={0}
          max={1.0}
          defaultValue={0.75}
          minLabel='Low'
          maxLabel='High'
          formatValue={(v) => `${(v * 100).toFixed(0)}%`}
        />
      </Box>
    </Box>
  </>
)

export default VoiceConfigElevenLabs
